import { useState } from "react";
import { motion } from "framer-motion";

const tintLevels = [
  { value: 0, label: "No Tint", darkness: 0, description: "Clear glass" },
  { value: 5, label: "5%", darkness: 0.95, description: "Darkest - Limo" },
  { value: 15, label: "15%", darkness: 0.85, description: "Very Dark" },
  { value: 20, label: "20%", darkness: 0.80, description: "Dark" },
  { value: 30, label: "30%", darkness: 0.70, description: "Medium Dark" },
  { value: 35, label: "35%", darkness: 0.65, description: "Medium" },
  { value: 50, label: "50%", darkness: 0.50, description: "Light" },
];

export const TintComparisonSlider = () => {
  const [selectedTint, setSelectedTint] = useState(20);
  const [sliderValue, setSliderValue] = useState(50);

  const currentTint = tintLevels.find(t => t.value === selectedTint) || tintLevels[0];

  return (
    <div className="tint-comparison-slider space-y-6" data-testid="tint-comparison-slider">
      {/* Main Comparison View */}
      <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[#1a1d24] p-6">
        <div className="space-y-4">
          <div className="text-center">
            <p className="text-sm text-blue-400 uppercase tracking-wider">Interactive Tint Preview</p>
            <h3 className="text-2xl font-medium text-white mt-2">
              See the Difference - Driver's View
            </h3>
            <p className="text-sm text-zinc-400 mt-1">
              Drag the slider or select a percentage to compare tint levels
            </p>
          </div>

          {/* Car Interior Window View */}
          <div className="relative h-[500px] rounded-[1.5rem] overflow-hidden bg-black">
            {/* Real Car Window Photo Background */}
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: 'url("https://customer-assets.emergentagent.com/job_export-station/artifacts/9kbosqs4_eren-goldman-RebG767l5_c-unsplash.jpg")',
              }}
            />

            {/* Split View Container */}
            <div className="absolute inset-0 flex">
              {/* Left Side - No Tint */}
              <div 
                className="relative overflow-hidden"
                style={{ width: `${sliderValue}%` }}
              >
                {/* Subtle window glass reflection */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/8 via-transparent to-transparent pointer-events-none" />
                
                {/* Label */}
                <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm px-5 py-2.5 rounded-full shadow-lg z-10">
                  <p className="text-xs font-bold text-gray-800 uppercase tracking-wide">No Tint</p>
                </div>
              </div>

              {/* Right Side - With Tint */}
              <div className="relative flex-1 overflow-hidden">
                {/* Tint overlay */}
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-b from-black via-black to-black"
                  initial={{ opacity: currentTint.darkness }}
                  animate={{ opacity: currentTint.darkness }}
                  transition={{ duration: 0.4 }}
                />
                
                {/* Realistic tint color - slightly blue/gray */}
                <div 
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: `linear-gradient(to bottom, 
                      rgba(20, 30, 48, ${currentTint.darkness * 0.25}), 
                      rgba(15, 25, 40, ${currentTint.darkness * 0.3}))`,
                  }}
                />
                
                {/* Window reflection on tinted side */}
                <div className="absolute inset-0 bg-gradient-to-tr from-white/3 via-transparent to-blue-400/3 pointer-events-none" />
                
                {/* Label */}
                <div className="absolute top-6 right-6 bg-red-500/95 backdrop-blur-sm px-5 py-2.5 rounded-full shadow-lg z-10">
                  <p className="text-xs font-bold text-white uppercase tracking-wide">
                    {currentTint.label} Tint
                  </p>
                </div>
              </div>
            </div>

            {/* Slider Handle */}
            <div 
              className="absolute top-0 bottom-0 w-1 bg-white shadow-[0_0_20px_rgba(255,255,255,0.5)] z-20"
              style={{ left: `${sliderValue}%` }}
            >
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 bg-white rounded-full shadow-2xl flex items-center justify-center cursor-grab active:cursor-grabbing border-4 border-blue-500">
                <div className="flex gap-1">
                  <div className="w-1 h-8 bg-gray-400 rounded-full" />
                  <div className="w-1 h-8 bg-gray-400 rounded-full" />
                </div>
              </div>
            </div>

            {/* Slider Input */}
            <input
              type="range"
              min="0"
              max="100"
              value={sliderValue}
              onChange={(e) => setSliderValue(Number(e.target.value))}
              className="absolute inset-0 w-full h-full opacity-0 cursor-grab active:cursor-grabbing z-30"
              data-testid="tint-slider-input"
            />
          </div>

          {/* Info Display */}
          <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-white/5 border border-white/10">
            <div>
              <p className="text-sm text-zinc-400">Selected Tint Level</p>
              <p className="text-lg font-semibold text-white">{currentTint.label}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-zinc-400">Classification</p>
              <p className="text-lg font-semibold text-blue-400">{currentTint.description}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-zinc-400">Light Blocked</p>
              <p className="text-lg font-semibold text-red-400">{Math.round(currentTint.darkness * 100)}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tint Level Selection Buttons */}
      <div>
        <p className="text-sm text-zinc-400 mb-3 text-center">Popular Tint Levels</p>
        <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
          {tintLevels.map((tint) => (
            <motion.button
              key={tint.value}
              onClick={() => setSelectedTint(tint.value)}
              className={`relative px-4 py-3 rounded-xl border transition-all ${
                selectedTint === tint.value
                  ? "border-blue-500 bg-blue-500/20 text-white"
                  : "border-white/10 bg-white/5 text-zinc-300 hover:border-white/20 hover:bg-white/10"
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid={`tint-level-${tint.value}`}
            >
              <div className="text-center">
                <p className="text-lg font-bold">{tint.label}</p>
                <p className="text-[10px] text-zinc-400 mt-0.5">{tint.description}</p>
              </div>
              
              {/* Visual darkness indicator */}
              <div className="mt-2 h-2 rounded-full bg-white/20 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-gray-600 to-black rounded-full transition-all"
                  style={{ width: `${tint.darkness * 100}%` }}
                />
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Legal Notice */}
      <div className="rounded-xl bg-amber-500/10 border border-amber-500/20 p-4">
        <div className="flex items-start gap-3">
          <div className="text-2xl">⚠️</div>
          <div>
            <p className="text-sm font-medium text-amber-200">Legal Tint Limits Vary by State</p>
            <p className="text-xs text-amber-200/80 mt-1">
              Illinois law: Front windows must allow 35%+ light transmission. Rear windows and back glass can be any darkness. 
              We'll help ensure your tint is legal and looks great.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
